import { useEffect, useState, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  ShieldCheck, Bell, Download, Plus, AlertTriangle, Clock, CheckCircle2, Search,
} from "lucide-react";
import { fmtDate } from "@/lib/helpers";

export default function Compliance() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);

  const load = async () => {
    const { data } = await api.get(isAdmin ? "/compliance" : "/compliance/me");
    setItems(data);
    if (isAdmin) {
      const [c, u] = await Promise.all([api.get("/courses"), api.get("/users")]);
      setCourses(c.data.filter((x) => x.is_mandatory));
      setUsers(u.data.filter((x) => x.role === "employee"));
    }
  };
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const c = { compliant: 0, expiring: 0, overdue: 0, pending: 0 };
    (items || []).forEach((i) => { c[i.computed_status] = (c[i.computed_status] || 0) + 1; });
    return c;
  }, [items]);

  const sendReminders = async () => {
    try {
      const { data } = await api.post("/compliance/remind");
      toast.success(`${data.reminders_sent} reminder${data.reminders_sent === 1 ? "" : "s"} sent`);
    } catch (e) { toast.error("Failed to send reminders"); }
  };

  const exportCsv = () => {
    const rows = [["Learner", "Department", "Course", "Status", "Due/Expiry", "Days Left"]];
    (items || []).forEach((i) => rows.push([i.user_name, i.department || "", i.course?.title || "", i.computed_status, fmtDate(i.expiry_date || i.due_date), i.days_left ?? ""]));
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "compliance_audit.csv";
    a.click();
    toast.success("Audit report exported");
  };

  if (!items) return <Loading />;

  const s = q.trim().toLowerCase();
  const filtered = (filter === "all" ? items : items.filter((i) => i.computed_status === filter))
    .filter((i) => !s || (i.user_name||"").toLowerCase().includes(s) || (i.course?.title||"").toLowerCase().includes(s) || (i.department||"").toLowerCase().includes(s));

  return (
    <div>
      <PageHeader title="Compliance & Certification" subtitle={isAdmin ? "Mandatory training matrix with 30/60/90-day expiry horizon." : "Your mandatory training requirements and deadlines."} testid="compliance-header">
        {isAdmin && (
          <>
            <Button variant="outline" onClick={exportCsv} data-testid="export-audit-button"><Download className="h-4 w-4 mr-1.5" />Export</Button>
            <Button variant="outline" onClick={sendReminders} data-testid="send-reminders-button"><Bell className="h-4 w-4 mr-1.5" />Send Reminders</Button>
            <Button onClick={() => setAssignOpen(true)} data-testid="assign-training-button"><Plus className="h-4 w-4 mr-1.5" />Assign</Button>
          </>
        )}
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <FilterCard active={filter === "all"} onClick={() => setFilter("all")} label="All Items" value={items.length} icon={ShieldCheck} color="text-primary" testid="compliance-filter-all" />
        <FilterCard active={filter === "overdue"} onClick={() => setFilter("overdue")} label="Overdue" value={counts.overdue || 0} icon={AlertTriangle} color="text-red-600" testid="compliance-filter-overdue-badge" />
        <FilterCard active={filter === "expiring"} onClick={() => setFilter("expiring")} label="Expiring Soon" value={counts.expiring || 0} icon={Clock} color="text-amber-600" testid="compliance-filter-expiring-badge" />
        <FilterCard active={filter === "compliant"} onClick={() => setFilter("compliant")} label="Compliant" value={counts.compliant || 0} icon={CheckCircle2} color="text-emerald-600" testid="compliance-filter-compliant-badge" />
      </div>

      <div className="relative max-w-sm mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, course or department..." className="pl-9" data-testid="search-compliance-input" />
      </div>

      {filtered.length === 0 ? (
        <EmptyState message="No compliance items match this filter." icon={ShieldCheck} />
      ) : isAdmin ? (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Learner</TableHead><TableHead>Department</TableHead><TableHead>Course</TableHead>
                <TableHead>Status</TableHead><TableHead>Due / Expiry</TableHead><TableHead className="text-right">Days Left</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((i) => (
                <TableRow key={i.id} data-testid={`compliance-row-${i.id}`}>
                  <TableCell className="font-medium">{i.user_name}</TableCell>
                  <TableCell className="text-muted-foreground">{i.department || "—"}</TableCell>
                  <TableCell>{i.course?.title}</TableCell>
                  <TableCell><StatusBadge status={i.computed_status} /></TableCell>
                  <TableCell className="font-mono text-xs">{fmtDate(i.expiry_date || i.due_date)}</TableCell>
                  <TableCell className={`text-right font-mono text-sm ${i.days_left < 0 ? "text-red-600" : i.days_left < 30 ? "text-amber-600" : ""}`}>{i.days_left != null ? i.days_left : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {filtered.map((i) => (
            <Card key={i.id} className="p-5" data-testid={`compliance-card-${i.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{i.course?.category}</span>
                  <h3 className="font-heading font-semibold mt-0.5">{i.course?.title}</h3>
                </div>
                <StatusBadge status={i.computed_status} />
              </div>
              <div className="mt-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">{i.expiry_date ? "Expires" : "Due"}</p>
                  <p className="text-sm font-medium">{fmtDate(i.expiry_date || i.due_date)}</p>
                </div>
                {i.days_left != null && (
                  <div className={`text-right ${i.days_left < 0 ? "text-red-600" : i.days_left < 30 ? "text-amber-600" : "text-emerald-600"}`}>
                    <p className="font-heading text-2xl font-bold">{Math.abs(i.days_left)}</p>
                    <p className="text-xs">{i.days_left < 0 ? "days overdue" : "days left"}</p>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {isAdmin && <AssignDialog open={assignOpen} setOpen={setAssignOpen} courses={courses} users={users} onSaved={load} />}
    </div>
  );
}

function FilterCard({ active, onClick, label, value, icon: Icon, color, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`text-left rounded-xl border p-4 transition-all ${active ? "border-primary ring-1 ring-primary bg-accent/30" : "border-border hover:border-primary/40"}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">{label}</span>
        <Icon className={`h-4 w-4 ${color}`} />
      </div>
      <p className="font-heading text-3xl font-bold mt-2">{value}</p>
    </button>
  );
}

function AssignDialog({ open, setOpen, courses, users, onSaved }) {
  const [courseId, setCourseId] = useState("");
  const [due, setDue] = useState("");
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const save = async () => {
    if (!courseId || !due || selected.length === 0) return toast.error("Select course, due date and learners");
    setSaving(true);
    try {
      const { data } = await api.post("/compliance/assign", { user_ids: selected, course_id: courseId, due_date: new Date(due).toISOString() });
      toast.success(`Assigned to ${data.created} learner${data.created === 1 ? "" : "s"}`);
      setOpen(false); setSelected([]); setCourseId(""); setDue("");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Assign Mandatory Training</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Mandatory Course</Label>
            <Select value={courseId} onValueChange={setCourseId}>
              <SelectTrigger className="mt-1.5" data-testid="assign-course-select"><SelectValue placeholder="Select course" /></SelectTrigger>
              <SelectContent>{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Due Date</Label><Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="mt-1.5" data-testid="assign-due-input" /></div>
          <div>
            <Label>Learners ({selected.length} selected)</Label>
            <div className="mt-1.5 border border-border rounded-lg divide-y divide-border max-h-60 overflow-y-auto">
              {users.map((u) => (
                <label key={u.id} className="flex items-center gap-3 p-2.5 cursor-pointer hover:bg-accent/30" data-testid={`assign-user-${u.id}`}>
                  <Checkbox checked={selected.includes(u.id)} onCheckedChange={() => toggle(u.id)} />
                  <div><p className="text-sm font-medium">{u.name}</p><p className="text-xs text-muted-foreground">{u.department || "—"}</p></div>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-assign-button">{saving ? "Assigning..." : "Assign Training"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
